import { useMemo } from "react";
import { ColorType, GrProgressBar, GrStack, GrTypography } from "@jnpmedi/grebob";
import { MvnMessage } from "@newLibs/utils";
import { useFolderContext } from "@newHooks/context/folder/folder.context";
import { useBrowseFoldersQuery } from "@newHooks/query/folder/browseFolders.query";
import { DOCS_DOCUMENT_LANG } from "@maven-msg/lang/docs";
import { FolderType } from "@maven-rest/docs/Folder.dto";
import { StorageUsage } from "@maven-rest/docs/FolderFile.dto";
import { FileHelper } from "@maven-helper/helpers/File.helper";

interface ArchiverUsageBarProps {
  storageUsage: StorageUsage;
  organizationId: bigint;
  isDeletedResource: boolean;
}

export function ArchiverUsageBar({ storageUsage, organizationId, isDeletedResource }: ArchiverUsageBarProps) {
  const { currentFolderId } = useFolderContext();

  const { error } = useBrowseFoldersQuery({
    organizationId,
    folderType: FolderType.ARCHIVER,
    folderId: currentFolderId,
    includeDeletedResource: isDeletedResource,
  });

  const { progressValue, progressBarColor, totalSize, totalUnit, usageSize, usageUnit } = useMemo(() => {
    const progressValue = (storageUsage.usage / (storageUsage.total || 1)) * 100;
    const range = Math.floor(progressValue / 10);
    const progressBarColor: ColorType = (() => {
      switch (range) {
        case 10:
        case 9:
          return "negative-primary";
        case 8:
        case 7:
          return "warning-primary";
        case 6:
        case 5:
        case 4:
        case 3:
        case 2:
        case 1:
        case 0:
        default:
          return "darkNavy040";
      }
    })();
    return {
      progressValue,
      progressBarColor,
      totalSize: FileHelper.readableFileSize(storageUsage.total, { decimalPlaces: 2 }).size,
      totalUnit: FileHelper.readableFileSize(storageUsage.total, { decimalPlaces: 2 }).unit,
      usageSize: FileHelper.readableFileSize(storageUsage.usage, { decimalPlaces: 2 }).size,
      usageUnit: FileHelper.readableFileSize(storageUsage.usage, { decimalPlaces: 2 }).unit,
    };
  }, [storageUsage.total, storageUsage.usage]);

  if (error?.response?.data?.code === "BILLING_FUNCTIONALITY_NOT_SUBSCRIBED") {
    return null;
  }

  return (
    <GrStack flexDirection="column" gap={6} width={200}>
      <GrProgressBar value={progressValue} color={progressBarColor} />
      <GrStack flexDirection="row" width={"100%"} justifyContent="space-between">
        <GrTypography type="Content.Caption02" color="grey050">
          {MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_ARCHIVER_STORAGE_USAGE, { size: usageSize, unit: usageUnit })}
        </GrTypography>
        <GrTypography type="Content.Caption02" color="grey050">
          {MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_ARCHIVER_STORAGE_TOTAL, { size: totalSize, unit: totalUnit })}
        </GrTypography>
      </GrStack>
    </GrStack>
  );
}
